import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useInventory } from './hooks/useInventory';
import InventoryLayout from './InventoryLayout';
import InventoryNavigation from './InventoryNavigation';
import InventoryBackButton from './InventoryBackButton';
import { getPlatformClass } from '../../utils/platformUtils';
import LoadingScreen from '../LoadingScreen';
import PlatformText from '../PlatformText';
import PlatformButton from '../PlatformButton';
import './InventoryDashboard.css';

function InventoryDashboard() {
  const navigate = useNavigate();
  const { userRole, isLoading: orgLoading } = useOrganization();
  const [currentTab, setCurrentTab] = useState('DASHBOARD');
  const platformClass = getPlatformClass();
  const isMobilePlatform = platformClass.includes('ios') || platformClass.includes('android') || platformClass.includes('pwa');

  // Get inventory data
  const {
    products,
    stockMovements,
    loading: inventoryLoading,
    error: inventoryError,
    getInventoryStats,
    getLowStockProducts,
    getRecentMovements
  } = useInventory();

  // Calculate dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalProducts: 0,
    totalValue: 0,
    lowStockItems: 0,
    recentMovements: 0
  });

  useEffect(() => {
    if (products && stockMovements) {
      const stats = getInventoryStats();
      const lowStockProducts = getLowStockProducts();
      const recentMovementsList = getRecentMovements(5); // Last 5 movements

      setDashboardStats({
        totalProducts: products.length,
        totalValue: stats.totalStockValue || 0,
        lowStockItems: lowStockProducts.length,
        recentMovements: recentMovementsList.length
      });
    }
  }, [products, stockMovements, getInventoryStats, getLowStockProducts, getRecentMovements]);

  // Show loading state if organization or inventory is loading
  if (orgLoading || inventoryLoading) {
    return <LoadingScreen message="Loading inventory..." variant="fullscreen" />;
  }

  // Check if user has access to inventory
  const hasInventoryAccess = ['InventoryManager', 'BusinessOwner', 'Accountant', 'Salesperson'].includes(userRole);
  
  if (!hasInventoryAccess) {
    return (
      <InventoryLayout>
        <div className="inventory-error-container">
          <h3>Access Denied</h3>
          <p>You don't have permission to access inventory management.</p>
          <PlatformButton onClick={() => navigate('/orders')}>
            Go to Orders
          </PlatformButton>
        </div>
      </InventoryLayout>
    );
  }

  // Show error if there's an inventory error
  if (inventoryError) {
    return (
      <InventoryLayout>
        <div className="inventory-error-container">
          <h3>Error Loading Inventory</h3>
          <p>{inventoryError}</p>
          <PlatformButton onClick={() => window.location.reload()}>
            Retry
          </PlatformButton>
        </div>
      </InventoryLayout>
    );
  }

  return (
    <InventoryLayout>
      <div className={`inventory-dashboard-container ${platformClass}`}>
        {/* Mobile App Bar */}
        {isMobilePlatform && (
          <div className="mobile-app-bar inventory-mobile-app-bar">
            <InventoryBackButton>Inventory Dashboard</InventoryBackButton>
          </div>
        )}

        {/* Desktop Header */}
        {!isMobilePlatform && (
          <div className="inventory-page-header">
            <div>
              <InventoryBackButton>Inventory Dashboard</InventoryBackButton>
              <PlatformText variant="body" className="inventory-page-subtitle">
                Manage your inventory and track stock levels
              </PlatformText>
            </div>
          </div>
        )}

        {/* Inventory Navigation */}
        <InventoryNavigation
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          userRole={userRole}
        />

        {/* Dashboard Content */}
        <div className="inventory-dashboard-content">
          <div className="inventory-stats-grid">
            <div className="inventory-stat-card">
              <div className="stat-icon">📦</div>
              <div className="stat-content">
                <div className="stat-value">{dashboardStats.totalProducts}</div>
                <div className="stat-label">Total Products</div>
              </div>
            </div>
            
            <div className="inventory-stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-value">₹{dashboardStats.totalValue.toLocaleString()}</div>
                <div className="stat-label">Total Value</div>
              </div>
            </div>
            
            <div className="inventory-stat-card">
              <div className="stat-icon">⚠️</div>
              <div className="stat-content">
                <div className="stat-value">{dashboardStats.lowStockItems}</div>
                <div className="stat-label">Low Stock Items</div>
              </div>
            </div>
            
            <div className="inventory-stat-card">
              <div className="stat-icon">📈</div>
              <div className="stat-content">
                <div className="stat-value">{dashboardStats.recentMovements}</div>
                <div className="stat-label">Recent Movements</div>
              </div>
            </div>
          </div>

          {products.length === 0 && (
            <div className="inventory-empty-container">
              <h3>Welcome to Inventory Management</h3>
              <p>This is where you'll manage your products and track stock levels.</p>
              <div className="inventory-empty-actions">
                <PlatformButton onClick={() => navigate('/inventory/products')}>
                  Add Your First Product
                </PlatformButton>
                <PlatformButton 
                  onClick={() => navigate('/inventory/stock-in')}
                  className="secondary"
                >
                  Stock In
                </PlatformButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </InventoryLayout>
  );
}

export default InventoryDashboard; 